import { useEffect, useId, useRef, useState } from 'react';
import { Button, Drawer, Input, Radio, Space, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { space } from '@viq-admin/ui';
import { CRM_COPY } from './copy';
import { HelpHint, HintLine } from './HelpHint';
import type { FieldChoice, FieldDecision, LeadEditPatch, MergePolicy, PersonPreview } from './types';

export interface MatchDetailsDrawerProps {
  /** The row whose Details are open; `null` closes the drawer. Read from the
   *  live `previews` by the caller, so a refetch re-renders fresh decisions. */
  person: PersonPreview | null;
  choices: Record<string, FieldChoice> | undefined;
  canManage: boolean;
  /** `policyOverride ??` the tenant default; undefined until the preview lands
   *  and treated as `fillBlanksOnly` (the safe display). */
  effectivePolicy?: MergePolicy;
  /** Spec 2026-09-28 §5.8: the company's settings forbid overwriting — conflicts show Keep CRM, inert, with a note. */
  overwriteLocked?: boolean;
  onPick: (field: string, choice: FieldChoice) => void;
  onClose: () => void;
  /**
   * Saves a new VendoIQ value for one field (gh-31 §4.9). Absent → no
   * pencils (read-only drawer). The caller re-reads the CRM afterwards; the
   * drawer shows `savedPendingRefresh` until fresh `decisions` arrive.
   */
  onEditOurs?: (field: keyof LeadEditPatch, value: string) => Promise<void>;
}

/** Reasons that render as a Keep-CRM / Use-ours pick. */
const CONFLICT_REASONS = new Set<FieldDecision['reason']>([
  'conflict_default',
  'conflict_choice',
  'policy_overwrite',
]);

const VALUE_WRAP: React.CSSProperties = { whiteSpace: 'normal', wordBreak: 'break-word' };

/** `FieldDecision.field` → the tear-sheet field it edits. Fields not listed
 *  (`lead_source`, `description`) are CRM-side vocabulary with no VendoIQ
 *  input behind them, so they get no pencil. */
const EDITABLE_FIELDS: Record<string, keyof LeadEditPatch> = {
  first_name: 'firstName',
  last_name: 'lastName',
  title: 'title',
  company: 'company',
  email: 'email',
  phone: 'phone',
};

/** Stable identity so the `pendingFields` reset effect below (keyed on
 *  `decisions`) does not fire on every render for a person with no
 *  decisions — `person?.decisions ?? []` would mint a fresh array each time. */
const EMPTY_DECISIONS: FieldDecision[] = [];

function labelFor(field: string): string {
  const labels: Record<string, string> = CRM_COPY.fieldLabel;
  return labels[field] ?? field;
}

function isConflict(d: FieldDecision): boolean {
  return d.action !== 'fill' && CONFLICT_REASONS.has(d.reason);
}

/** A field with nothing to decide — shown greyed so the comparison is complete
 *  without competing with the rows that matter. */
function isQuiet(d: FieldDecision): boolean {
  return !isConflict(d) && d.action !== 'fill' && d.reason !== 'not_patchable';
}

/**
 * Match's Details drawer (gh-31 §4.9): one person, every field the merge
 * engine compared, side by side — Field | CRM says | We say | What will
 * happen. Equal fields are shown too (greyed, "Same"), so a fully-matched
 * record no longer looks like a dead button; the old inline panel hid them
 * and rendered nothing at all.
 *
 * Picks keep the inline panel's semantics exactly: BOTH radios record an
 * explicit pick through their own `onClick` (antd fires the group's
 * `onChange` only on a value CHANGE, so clicking the side already shown as
 * the default would otherwise record nothing — and a later per-run Overwrite
 * would flip the very field the operator clicked to keep). With no pick, the
 * pre-selected side is what the commit would actually do under
 * `effectivePolicy`, and an unpicked conflict under `overwrite` carries
 * `overwriteDefaultNote`. Radios are named `"{field}: Keep CRM"` /
 * `"{field}: Use ours"` so several conflicts stay distinguishable.
 */
export function MatchDetailsDrawer({
  person,
  choices,
  canManage,
  effectivePolicy,
  overwriteLocked,
  onPick,
  onClose,
  onEditOurs,
}: MatchDetailsDrawerProps) {
  const panelId = useId();
  const decisions = person?.decisions ?? EMPTY_DECISIONS;
  const [editingField, setEditingField] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [busyField, setBusyField] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<string | null>(null);
  // Fields saved but not yet reflected — cleared the moment fresh decisions
  // arrive (the caller invalidates both queries after a save, and the person
  // prop is read off the live previews, so `decisions` changes identity).
  const [pendingFields, setPendingFields] = useState<Set<string>>(new Set());
  useEffect(() => {
    setPendingFields((prev) => (prev.size ? new Set() : prev));
  }, [decisions]);
  // Always the CURRENT person, readable from inside a `saveEdit` closure
  // captured before a switch — its `await` can outlive the render that
  // started it, and that closure's own `person` variable is frozen at
  // whatever it was then, so it can't see a later switch on its own
  // (review fix — single-slot ownership, gh-31).
  const personIdRef = useRef(person?.personId);
  personIdRef.current = person?.personId;
  // A different person → none of the last one's half-typed draft, busy/error
  // state, or pending note carries across (review fix — this used to leave
  // busyField/draft/pendingFields behind, so a save that resolved AFTER the
  // switch could mark its field pending on the new person's row instead).
  useEffect(() => {
    setEditingField(null);
    setErrorField(null);
    setBusyField(null);
    setDraft('');
    setPendingFields((prev) => (prev.size ? new Set() : prev));
  }, [person?.personId]);

  async function saveEdit(field: string) {
    const key = EDITABLE_FIELDS[field];
    if (!onEditOurs || !key) return;
    const startedFor = personIdRef.current;
    setBusyField(field);
    setErrorField(null);
    try {
      await onEditOurs(key, draft.trim());
      // The person switched while this save was in flight — its result
      // belongs to nobody on screen now (the person-change effect above
      // already reset this field's UI state); leave it alone rather than
      // marking it pending on whoever is showing today (review fix).
      if (personIdRef.current !== startedFor) return;
      setPendingFields((prev) => new Set(prev).add(field));
      setEditingField((cur) => (cur === field ? null : cur));
    } catch {
      // Same ownership guard on failure: don't report an error against a
      // field on a person no longer on screen.
      if (personIdRef.current === startedFor) setErrorField(field);
    } finally {
      // Person-owned too (review fix, round 2): on a mismatch the
      // person-change effect already reset busyField for whoever is on
      // screen now — clearing it here on field-name match ALONE would
      // unlock a same-named field's pencils for a DIFFERENT person while
      // THEIR own save for it is still in flight (reopens consequence a).
      if (personIdRef.current === startedFor) {
        setBusyField((cur) => (cur === field ? null : cur));
      }
    }
  }

  const hasConflict = decisions.some(isConflict);
  const firstFillField = decisions.find((d) => d.action === 'fill')?.field;
  const firstNotPatchableField = decisions.find(
    (d) => d.action !== 'fill' && d.reason === 'not_patchable',
  )?.field;
  const remoteType =
    person?.remoteType === 'lead'
      ? CRM_COPY.remoteTypeLabel.lead
      : person?.remoteType === 'contact'
        ? CRM_COPY.remoteTypeLabel.contact
        : null;

  const renderValue = (v: string | null, quiet?: boolean) =>
    v ? (
      <Typography.Text type={quiet ? 'secondary' : undefined} style={VALUE_WRAP}>
        {v}
      </Typography.Text>
    ) : (
      <Typography.Text type="secondary">—</Typography.Text>
    );

  const renderOutcome = (d: FieldDecision) => {
    const label = labelFor(d.field);
    if (d.action === 'fill') {
      return (
        <Space size={space.xs} wrap>
          <Tag color="success">{CRM_COPY.detailsWillAdd}</Tag>
          {d.field === firstFillField && (
            <HelpHint {...CRM_COPY.help.matchWillAdd} testId="crm-help-diff-will-add" />
          )}
        </Space>
      );
    }
    if (d.reason === 'not_patchable') {
      return (
        <Space size={space.xs} wrap>
          <Typography.Text type="secondary">{`${CRM_COPY.notPatchablePrefix} ${label}`}</Typography.Text>
          {d.field === firstNotPatchableField && (
            <HelpHint {...CRM_COPY.help.matchNotPatchable} testId="crm-help-diff-not-patchable" />
          )}
        </Space>
      );
    }
    if (isConflict(d)) {
      const explicit = overwriteLocked ? undefined : choices?.[d.field];
      const picked: FieldChoice = overwriteLocked
        ? 'theirs'
        : (explicit ?? (effectivePolicy === 'overwrite' ? 'ours' : 'theirs'));
      const showOverwriteNote = !overwriteLocked && !explicit && effectivePolicy === 'overwrite';
      const canPick = canManage && !overwriteLocked;
      return (
        <div>
          <Radio.Group
            name={`${panelId}-${d.field}`}
            value={picked}
            disabled={!canPick}
            onChange={(e) => onPick(d.field, e.target.value as FieldChoice)}
          >
            <Space direction="vertical" size={space.xs}>
              <Radio
                value="theirs"
                aria-label={`${label}: ${CRM_COPY.detailsKeepCrm}`}
                onClick={() => {
                  if (canPick) onPick(d.field, 'theirs');
                }}
              >
                {CRM_COPY.detailsKeepCrm}
              </Radio>
              <Radio
                value="ours"
                aria-label={`${label}: ${CRM_COPY.detailsUseOurs}`}
                onClick={() => {
                  if (canPick) onPick(d.field, 'ours');
                }}
              >
                {CRM_COPY.detailsUseOurs}
              </Radio>
            </Space>
          </Radio.Group>
          {showOverwriteNote && (
            <Typography.Text type="warning" style={{ display: 'block', marginTop: space.xs, fontSize: 12 }}>
              {CRM_COPY.overwriteDefaultNote}
            </Typography.Text>
          )}
          {overwriteLocked && (
            <Typography.Text type="secondary" style={{ display: 'block', marginTop: space.xs, fontSize: 12 }}>
              {CRM_COPY.detailsLockedNote}
            </Typography.Text>
          )}
        </div>
      );
    }
    if (d.reason === 'equal') {
      return <Typography.Text type="secondary">{CRM_COPY.detailsSame}</Typography.Text>;
    }
    // ours_blank / both_blank (and any reason the engine adds later) —
    // nothing we can offer.
    return <Typography.Text type="secondary">{CRM_COPY.detailsNothingToAdd}</Typography.Text>;
  };

  const columns: NonNullable<TableProps<FieldDecision>['columns']> = [
    {
      key: 'field',
      title: CRM_COPY.detailsFieldHeader,
      width: 120,
      render: (_: unknown, d: FieldDecision) => (
        <Typography.Text strong={!isQuiet(d)} type={isQuiet(d) ? 'secondary' : undefined}>
          {labelFor(d.field)}
        </Typography.Text>
      ),
    },
    {
      key: 'theirs',
      title: CRM_COPY.crmSaysLabel,
      render: (_: unknown, d: FieldDecision) => renderValue(d.theirs, isQuiet(d)),
    },
    {
      key: 'ours',
      title: CRM_COPY.weSayLabel,
      render: (_: unknown, d: FieldDecision) => {
        const label = labelFor(d.field);
        const editable = canManage && !!onEditOurs && !!EDITABLE_FIELDS[d.field];
        // One edit at a time (single-slot machine, kept — not extracted this
        // round): a pencil stays live only while nothing anywhere in the
        // table is mid-save, and this field itself isn't awaiting its
        // post-save refresh (review fix).
        const pencilLocked = busyField !== null || pendingFields.has(d.field);
        if (editingField === d.field) {
          const trimmed = draft.trim();
          const canSave = trimmed !== '' && trimmed !== (d.ours ?? '');
          return (
            <Space direction="vertical" size={space.xs} style={{ width: '100%' }}>
              <Space size={space.xs} wrap>
                <Input
                  size="small"
                  value={draft}
                  aria-label={`New value for ${label}`}
                  onChange={(e) => setDraft(e.target.value)}
                  onPressEnter={() => {
                    if (canSave) void saveEdit(d.field);
                  }}
                  disabled={busyField === d.field}
                  style={{ minWidth: 160 }}
                />
                <Button
                  size="small"
                  type="primary"
                  loading={busyField === d.field}
                  disabled={!canSave}
                  onClick={() => void saveEdit(d.field)}
                >
                  {CRM_COPY.saveButton}
                </Button>
                <Button size="small" onClick={() => setEditingField(null)}>
                  {CRM_COPY.detailsCancel}
                </Button>
              </Space>
              {errorField === d.field && (
                <Typography.Text type="danger">{CRM_COPY.saveFieldFailed}</Typography.Text>
              )}
            </Space>
          );
        }
        return (
          <Space direction="vertical" size={space.xs} style={{ width: '100%' }}>
            <Space size={space.xs} wrap>
              {renderValue(d.ours, isQuiet(d))}
              {pendingFields.has(d.field) && (
                <Typography.Text type="secondary">{CRM_COPY.savedPendingRefresh}</Typography.Text>
              )}
              {editable && (
                <>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined aria-hidden="true" />}
                    aria-label={`Edit ${label}`}
                    disabled={pencilLocked}
                    onClick={() => {
                      setEditingField(d.field);
                      setDraft(d.ours ?? '');
                      setErrorField(null);
                    }}
                  />
                  {d.field === decisions.find((x) => EDITABLE_FIELDS[x.field])?.field && (
                    <HelpHint {...CRM_COPY.help.matchEditOurs} testId="crm-help-diff-edit" />
                  )}
                </>
              )}
            </Space>
            {/* Rendered here too (review fix), not just inside the editing
                branch above — a save can fail after the user already
                cancelled or moved on, and the failure must not be silently
                swallowed. The pencil stays live so they can retry. */}
            {errorField === d.field && (
              <Typography.Text type="danger">{CRM_COPY.saveFieldFailed}</Typography.Text>
            )}
          </Space>
        );
      },
    },
    {
      key: 'outcome',
      title: CRM_COPY.detailsOutcomeHeader,
      width: 200,
      render: (_: unknown, d: FieldDecision) => renderOutcome(d),
    },
  ];

  return (
    <Drawer
      open={person !== null}
      onClose={onClose}
      width={720}
      destroyOnHidden
      title={
        person ? (
          <Space size={space.sm}>
            <span>{person.displayName}</span>
            {remoteType && <Tag>{remoteType}</Tag>}
          </Space>
        ) : null
      }
    >
      {person && (
        <div>
          {hasConflict && (
            <HintLine text={CRM_COPY.help.matchDiffIntro.text} testId="crm-hint-match-diff">
              <HelpHint {...CRM_COPY.help.matchCrmSays} testId="crm-help-diff-crm-says" />
              <HelpHint {...CRM_COPY.help.matchWeSay} testId="crm-help-diff-we-say" />
            </HintLine>
          )}
          {decisions.length === 0 ? (
            <Typography.Text type="secondary" data-testid="crm-details-empty">
              {CRM_COPY.detailsEmptyState}
            </Typography.Text>
          ) : (
            <Table<FieldDecision>
              rowKey="field"
              size="small"
              pagination={false}
              dataSource={decisions}
              columns={columns}
              onRow={(d) =>
                ({ 'data-testid': `crm-details-row-${d.field}` }) as React.HTMLAttributes<HTMLElement>
              }
            />
          )}
        </div>
      )}
    </Drawer>
  );
}
